import React, { useState, useEffect, useCallback } from 'react';
import { useWeb3React } from '@web3-react/core';
import { 
  getPartyAddresses,
  getContractState,
  submitPriceB,
  submitPriceE,
  signByB,
  signByE,
  payInterest,
  repayLoan
} from '../utils/contract';

const TransferForm = () => {
  const { active, account, library } = useWeb3React();
  const [loading, setLoading] = useState(false);
  const [txResult, setTxResult] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [contractState, setContractState] = useState({ stateNumber: 0, stateName: 'Loading...' });
  const [actionOptions, setActionOptions] = useState([]);
  const [selectedAction, setSelectedAction] = useState(null);
  const [amount, setAmount] = useState('');
  const [price, setPrice] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [partyAddresses, state] = await Promise.all([
        getPartyAddresses(),
        getContractState()
      ]);
      
      setContractState(state);
      
      if (active && account) {
        const accountLower = account.toLowerCase();
        if (accountLower === partyAddresses.partyN?.toLowerCase()) {
          setUserRole('N');
        } else if (accountLower === partyAddresses.partyB?.toLowerCase()) {
          setUserRole('B');
        } else if (accountLower === partyAddresses.partyE?.toLowerCase()) {
          setUserRole('E');
        } else {
          setUserRole(null);
        }
      } else {
        setUserRole(null);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [active, account]);

  // Determine user role and contract state
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Determine available action options based on user role and contract state
  useEffect(() => {
    const options = [];
    
    if (!userRole || !contractState) return;
    
    // PENDING state actions
    if (contractState.stateNumber === 0) { // PENDING
      // Party B can submit price
      if (userRole === 'B') {
        options.push({
          id: 'submitPriceB',
          name: 'Submit Price',
          description: 'Submit NILA token price as Party B',
          action: submitPriceB,
          requiresPrice: true
        });
      }
      
      // Party E can submit price
      if (userRole === 'E') {
        options.push({
          id: 'submitPriceE',
          name: 'Submit Price',
          description: 'Submit NILA token price as Party E',
          action: submitPriceE,
          requiresPrice: true
        });
      }
    }
    
    // PRICE_SET state actions
    if (contractState.stateNumber === 1) { // PRICE_SET
      // Party B can sign
      if (userRole === 'B') {
        options.push({
          id: 'signByB',
          name: 'Sign Agreement',
          description: 'Sign the agreement as Party B',
          action: signByB
        });
      }
      
      // Party E can sign
      if (userRole === 'E') {
        options.push({
          id: 'signByE',
          name: 'Sign Agreement',
          description: 'Sign the agreement as Party E',
          action: signByE
        });
      }
    }
    
    // ACTIVE state actions
    if (contractState.stateNumber === 2) { // ACTIVE
      // Party E can pay interest
      if (userRole === 'E') {
        options.push({
          id: 'payInterest',
          name: 'Pay Interest',
          description: 'Pay interest on the loan',
          action: payInterest,
          requiresAmount: true
        });
        
        options.push({
          id: 'repayLoan',
          name: 'Repay Loan',
          description: 'Repay the loan principal and final interest',
          action: repayLoan
        });
      }
    }
    
    setActionOptions(options);
    setSelectedAction(null);
    setAmount('');
    setPrice('');
  }, [userRole, contractState]);

  const handleActionSelect = (option) => {
    setSelectedAction(option);
  };

  const handleAmountChange = (e) => {
    // Only allow numbers and decimals
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setAmount(value);
    }
  };

  const handlePriceChange = (e) => {
    // Only allow numbers and decimals
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setPrice(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!active || !account || !selectedAction) {
      return;
    }
    
    setLoading(true);
    setTxResult(null);
    
    try {
      let result;
      
      if (selectedAction.requiresAmount) {
        if (!amount || parseFloat(amount) <= 0) {
          throw new Error('Please enter a valid amount');
        }
        result = await selectedAction.action(library, account, amount);
      } else if (selectedAction.requiresPrice) {
        if (!price || parseFloat(price) <= 0) {
          throw new Error('Please enter a valid price');
        }
        result = await selectedAction.action(library, account, price);
      } else {
        result = await selectedAction.action(library, account);
      }
      
      if (result.success) {
        setTxResult({
          success: true,
          message: `${selectedAction.name} successful!`,
          hash: result.hash
        });
        setAmount('');
        setPrice('');
        setSelectedAction(null);
      } else {
        setTxResult({
          success: false,
          message: `${selectedAction.name} failed: ${result.error}`
        });
      }
    } catch (error) {
      console.error('Error in transaction:', error);
      setTxResult({
        success: false,
        message: `Error: ${error.message || 'Transaction failed'}`
      });
    } finally {
      setLoading(false);
    }
  };

  if (!active || !account) {
    return (
      <div className="transfer-form">
        <h2>Contract Actions</h2>
        <p>Please connect your wallet to perform contract actions.</p>
      </div>
    );
  }

  if (!userRole) {
    return (
      <div className="transfer-form">
        <h2>Contract Actions</h2>
        <p>Your wallet is not authorized to perform actions in this contract.</p>
        <p>Only parties N, B, and E can interact with this contract.</p>
      </div>
    );
  }

  if (actionOptions.length === 0) {
    return (
      <div className="transfer-form">
        <h2>Contract Actions</h2>
        <p>No actions are currently available for your role (Party {userRole}).</p>
        <p>Contract state: {contractState.stateName}</p>
      </div>
    );
  }

  return (
    <div className="transfer-form">
      <h2>Contract Actions</h2>
      
      <div className="info-note">
        <p>
          <strong>Contract State:</strong> {contractState.stateName}
        </p>
        <p>
          <strong>Your Role:</strong> Party {userRole}
        </p>
        <button onClick={fetchData} className="refresh-button">Refresh</button>
      </div>
      
      {!selectedAction ? (
        <div className="action-options">
          <p>Select an action to perform:</p>
          {actionOptions.map(option => (
            <div key={option.id} className="action-option">
              <h3>{option.name}</h3>
              <p>{option.description}</p>
              <button 
                onClick={() => handleActionSelect(option)} 
                className="submit-button"
              >
                Select
              </button>
            </div>
          ))}
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <h3>{selectedAction.name}</h3>
          <p>{selectedAction.description}</p>
          
          {selectedAction.requiresAmount && (
            <div className="form-group">
              <label htmlFor="amount">Amount:</label>
              <input
                id="amount"
                type="text"
                value={amount}
                onChange={handleAmountChange}
                placeholder="0.0"
                disabled={loading}
                className="form-control"
              />
            </div>
          )}
          
          {selectedAction.requiresPrice && (
            <div className="form-group">
              <label htmlFor="price">NILA Price (USD):</label>
              <input
                id="price"
                type="text"
                value={price}
                onChange={handlePriceChange}
                placeholder="0.0"
                disabled={loading}
                className="form-control"
              />
            </div>
          )}
          
          <div className="form-actions">
            <button 
              type="button" 
              onClick={() => setSelectedAction(null)}
              disabled={loading}
              className="back-button"
            >
              Back
            </button>
            
            <button 
              type="submit" 
              disabled={loading || (selectedAction.requiresAmount && (!amount || parseFloat(amount) <= 0)) || (selectedAction.requiresPrice && (!price || parseFloat(price) <= 0))}
              className="submit-button"
            >
              {loading ? 'Processing...' : 'Submit'}
            </button>
          </div>
        </form>
      )}
      
      {txResult && (
        <div className={`transaction-result ${txResult.success ? 'success' : 'error'}`}>
          <p>{txResult.message}</p>
          {txResult.hash && (
            <a 
              href={`https://testnet.bscscan.com/tx/${txResult.hash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="tx-link"
            >
              View Transaction
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default TransferForm;
